import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Divider, Flex, Skeleton, Typography } from 'antd';
import cn from 'classnames';

import { useAppDispatch } from '@/shared/lib/store';

import { useLazyGetAccountInfoQuery } from '../../api/trading-platform.api';
import { useGetTradingPlatformSessionId } from '../../models/selectors/get-trading-platform-session-id';
import { useGetTradingPlatformSessionType } from '../../models/selectors/get-trading-platform-session-type';
import { tradingPlatformSessionActions } from '../../models/slices/trading-platform-session.slice';

import cls from './trading-platform-connected-info.module.css';

export const TradingPlatformConnectedInfo = memo(() => {
    const { t } = useTranslation('trading-journal');

    const dispatch = useAppDispatch();

    const accountId = useGetTradingPlatformSessionId();
    const connectionType = useGetTradingPlatformSessionType();

    const [
        getAccountInfoQuery,
        { data: accountInfo, isFetching: isGetAccountInfoFetching },
    ] = useLazyGetAccountInfoQuery();

    useEffect(() => {
        if (accountId && connectionType) {
            getAccountInfoQuery({ accountId, connectionType });
        }
    }, [accountId, getAccountInfoQuery, connectionType]);

    useEffect(() => {
        if (accountInfo) {
            dispatch(
                tradingPlatformSessionActions.setConneciontSessionCurrency(
                    accountInfo.currency,
                ),
            );
        }
    }, [accountInfo, dispatch]);

    const connectionTypeText = useMemo(() => {
        switch (connectionType) {
            case 'mt4':
                return t('tradingJournalConnectionInfoPanelConnectionTypeMt4');
            case 'mt5':
                return t('tradingJournalConnectionInfoPanelConnectionTypeMt5');
            case 'cTrader':
                return t(
                    'tradingJournalConnectionInfoPanelConnectionTypeCTrader',
                );
            default:
                return 'Unknown';
        }
    }, [connectionType, t]);

    const onReconnect = useCallback(() => {
        dispatch(tradingPlatformSessionActions.recconect());
    }, [dispatch]);

    return (
        <Flex
            align="center"
            className={cls.root}
        >
            {isGetAccountInfoFetching ? (
                <Skeleton.Input
                    active
                    className={cls.text}
                    size="small"
                />
            ) : (
                <Typography.Text className={cls.text}>
                    {accountInfo?.login}
                </Typography.Text>
            )}
            <Divider
                orientationMargin={8}
                type="vertical"
            />
            <Typography.Text className={cn(cls.text, cls.connectionType)}>
                {connectionTypeText}
            </Typography.Text>
            <Button
                className={cls.button}
                danger
                disabled={isGetAccountInfoFetching}
                onClick={onReconnect}
                size="small"
                type="link"
            >
                {t('tradingJournalConnectionInfoPanelReconnect')}
            </Button>
        </Flex>
    );
});

TradingPlatformConnectedInfo.displayName = 'TradingPlatformConnectionStatus';
